import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { Prop } from '../../types';
import { createLastModifiedBy } from '../../utils/lastModifiedBy';
import { logger } from '../../utils/logger.ts';
import { deleteProp } from './props-api.ts';
import type { ProjectScope } from '../sync/project-scope.ts';
import {
    markPendingPropDelete,
    markPendingPropWrite,
} from '../sync/prop-pending-writes';

export interface PropsSlice {
    props: Prop[];
    propsActions: {
        addProp: (prop: Partial<Prop>) => string;
        updateProp: (id: string, updates: Partial<Prop>) => void;
        deleteProps: (ids: string[]) => void;
        clearProps: () => void; // Project scoping
    };
}

function getPropsProjectScope(state: AppState & PropsSlice): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

export const createPropsSlice: StateCreator<
    AppState & PropsSlice,
    [["zustand/immer", never]],
    [],
    PropsSlice
> = (set, get) => ({
    props: [],
    propsActions: {
        addProp: (prop) => {
            const id = crypto.randomUUID();
            set(state => {
                const lastModifiedBy = createLastModifiedBy(
                    state.auth.user,
                    state.cloud.member
                );
                
                const newProp: Prop = {
                    id,
                    name: prop.name || 'New Prop',
                    type: prop.type || 'Hand',
                    notes: '',
                    status: 'undefined',
                    images: [],
                    budget: { currency: 'USD' },
                    maintenance: {
                        needsRepair: false,
                        repairNotes: '',
                        needsLaundry: false,
                        maintenanceNotes: '',
                    },
                    source: {
                        source: 'stock',
                    },
                    ...prop,
                    lastModifiedBy
                };
                if (!state.props) state.props = [];
                state.props.push(newProp);
            });
            const scope = getPropsProjectScope(get());
            if (scope) {
                markPendingPropWrite(scope, id);
            }
            return id;
        },
        updateProp: (id, updates) => {
            const scope = getPropsProjectScope(get());
            let didUpdate = false;
            set(state => {
                const prop = state.props.find(p => p.id === id);
                if (prop) {
                    const lastModifiedBy = createLastModifiedBy(
                        state.auth.user,
                        state.cloud.member
                    );
                    Object.assign(prop, updates, { lastModifiedBy });
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingPropWrite(scope, id);
            }
        },
        deleteProps: (ids) => {
            const scope = getPropsProjectScope(get());
            set(state => {
                state.props = state.props.filter(p => !ids.includes(p.id));
            });
            if (scope) {
                for (const id of ids) {
                    markPendingPropDelete(scope, id);
                    void deleteProp(scope, id).catch(error => {
                        logger.error('[props-store] Failed to preserve prop deletion:', error);
                    });
                }
            }
        },
        clearProps: () => set(state => {
            state.props = [];
        })
    }
});











